import type { LifecycleSpec } from './lifecycle.js';

/**
 * The global water cycle, as net transport between three reservoirs.
 *
 * Oceans evaporate 413 and receive 373 thousand km³ a year, so they lose 40.
 * Land receives 113 in precipitation and returns 73 by evapotranspiration, so it
 * gains 40. Rivers return exactly that 40 to the sea. The same number crosses
 * every boundary, which is what "closed" means. The atmosphere holds only 12.7
 * thousand km³ against 486 of annual precipitation — about nine days' worth.
 */
export function lifecycleFixture(width = 640, activeStageId = 'atmosphere'): LifecycleSpec {
  return {
    id: 'global-water-cycle',
    family: 'primitive-lifecycle',
    view: 'schematic',
    viewport: { width, height: 470 },
    subject: 'Global water cycle',
    unit: '10³ km³/yr',
    stages: [
      { id: 'ocean', label: 'Ocean', stock: '1 335 000', detail: 'evaporates 413, receives 373' },
      { id: 'atmosphere', label: 'Atmosphere', stock: '12.7', detail: 'about nine days of water' },
      { id: 'land', label: 'Land', detail: 'receives 113, returns 73' },
    ],
    fluxes: [
      { id: 'net-evaporation', from: 'ocean', to: 'atmosphere', label: 'Net evaporation', quantity: 40 },
      { id: 'net-precipitation', from: 'atmosphere', to: 'land', label: 'Net precipitation', quantity: 40 },
      { id: 'runoff', from: 'land', to: 'ocean', label: 'River runoff', quantity: 40 },
    ],
    closure: 'No stage stores the difference · what the ocean loses, the rivers return',
    activeStageId,
    title: 'The same 40 000 km³ crosses every boundary each year',
    subtitle: 'A cycle is only a cycle if it closes: each reservoir returns exactly what it receives, and the return path is drawn as one.',
    provenance: 'Trenberth et al. (2007) global water-cycle estimates, thousand km³ per year; residence time derived from 12.7 / 486 yr',
  };
}
